import { useIndicatorsAnimal } from '@/api/hooks';
import { Card, CardBody } from '@/components/ui/Card';
import { formatDate } from '@/lib/format';
import { Link } from 'react-router-dom';

function formatGmd(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toFixed(2)} kg/dia`;
}

export function AnimalZootecnicosPanel({ animalId }: { animalId: number }) {
  const { data, isLoading, isError } = useIndicatorsAnimal(animalId);

  if (isLoading) {
    return <div className="h-40 rounded-2xl bg-manejai-sand/40 animate-pulse" />;
  }

  if (isError) {
    return (
      <Card>
        <CardBody className="text-sm text-red-600">Não foi possível carregar os indicadores zootécnicos.</CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 text-sm">
        <Stat label="Peso atual" value={data?.current_weight_kg != null ? `${data.current_weight_kg} kg` : '—'} />
        <Stat label="Última pesagem" value={formatDate(data?.last_weighing_date)} />
        <Stat label="GMD no período" value={formatGmd(data?.gmd_avg_kg_per_day)} />
        <Stat label="GMD vitalício" value={formatGmd(data?.gmd_lifetime_kg_per_day)} />
        <Link
          to="/zootecnia"
          className="ml-auto self-center text-sm text-manejai-moss hover:underline"
        >
          Ver indicadores da fazenda
        </Link>
      </div>

      {data?.reproduction && (
        <div className="flex flex-wrap gap-4 text-sm">
          <Stat label="Partos" value={String(data.reproduction.births_total ?? 0)} />
          <Stat label="IEP médio" value={data.reproduction.avg_iep_days != null ? `${Math.round(data.reproduction.avg_iep_days)} dias` : '—'} />
          <Stat label="Coberturas no período" value={String(data.reproduction.covers_in_period ?? 0)} />
        </div>
      )}

      {(data?.gmd_intervals.length ?? 0) === 0 ? (
        <Card>
          <CardBody className="py-10 text-center text-sm text-manejai-bark/55">
            Sem intervalos de peso suficientes para calcular GMD.
          </CardBody>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/55">
                  <th className="px-4 py-3 font-medium">Início</th>
                  <th className="px-4 py-3 font-medium">Fim</th>
                  <th className="px-4 py-3 font-medium">Dias</th>
                  <th className="px-4 py-3 font-medium">Ganho</th>
                  <th className="px-4 py-3 font-medium">GMD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-manejai-sand/50">
                {data!.gmd_intervals.map((row) => (
                  <tr key={`${row.start_date}-${row.end_date}`}>
                    <td className="px-4 py-3">{formatDate(row.start_date)}</td>
                    <td className="px-4 py-3">{formatDate(row.end_date)}</td>
                    <td className="px-4 py-3">{row.days}</td>
                    <td className="px-4 py-3">{row.weight_gain_kg} kg</td>
                    <td className="px-4 py-3 font-medium">{formatGmd(row.gmd_kg_per_day)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-manejai-cream/60 px-4 py-3">
      <p className="text-xs text-manejai-bark/55">{label}</p>
      <p className="font-semibold text-manejai-forest">{value}</p>
    </div>
  );
}
